import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SESSIONS_FIELD_KEY, toStoredMembershipProfileValue } from '@citizen-walk/shared-types';
import {
  EMPTY_USER_FIELDS_VALUES,
  findMissingRequired,
  SessionPicker,
  UserFieldsForm,
  useAnonymousAuth,
  type UserFieldsFormValues,
} from '../auth';
import { useDistrict, useDistrictState } from '../district';
import {
  getGeolocationPermissionState,
  requestLocation,
  requestOrientationPermission,
} from '../posts';
import { clearPendingSessionId, loadPendingSessionId, useCurrentSession } from '../sessions';
import { InstallPrompt } from '../shell';
import { functions } from '../../lib/firebase';
import { CONSENT_TEXT, CONSENT_VERSION } from './legalText';

/**
 * U1 初回設定（プロフィール＋同意モーダル＋権限取得モーダル）
 *
 * old2 `apps/user/src/pages/OnboardingPage.tsx` (356 行) の状態機械を逐語移植。
 *
 * フロー: プロフィール入力 (UserFieldsForm) + 参加セッション選択 → 「はじめる」(必須チェック)
 *   → 同意モーダル (CONSENT_TEXT 逐語) → handleAgree で createMembership callable
 *   → 権限取得モーダル (方位 → 位置の順次要求) → 位置成功で recovery/issue へ。
 *
 * v2 調整点:
 *   - route 要素として動作するよう内部で useDistrict / useAnonymousAuth / useCurrentSession を使う
 *     (旧 AppHome gate からの props 駆動を廃止)。
 *   - sessions は old2 の district.sessions ではなく v2 `useCurrentSession(districtId).state.items`
 *     から取得 (districts/{id}/sessions subscribe)。
 *   - 文言は old2 user.json / legal.json を逐語 (CONSENT_TEXT は legalText.ts、その他は直接埋込)。
 *   - createMembership 成功後の nickname ローカルキャッシュは省略 (v2 では useNickname / membership
 *     read が source of truth)。sessionStorage pendingSessionId 連携も v2 では未配線のため省略。
 */

export function OnboardingPage() {
  const navigate = useNavigate();
  const { urlToken } = useParams();
  const auth = useAnonymousAuth();
  const districtState = useDistrictState();
  const district = useDistrict();
  const user = auth.status === 'authenticated' ? auth.user : null;

  const loading =
    !districtState || districtState.status === 'loading' || districtState.status === 'idle';
  const error = districtState?.status === 'error' ? districtState.error.message : null;

  const { state: sessionState } = useCurrentSession(district?.id ?? null);
  const sessions = sessionState.status === 'ready' ? sessionState.items : [];
  const userFields = district?.dataSettings?.userFields ?? [];
  // 2026-09-05: 参加セッションの項目定義 (択一 / 複数・表示タイプ・項目名)。SessionPicker に渡す。
  const sessionsField = userFields.find((f) => f.key === SESSIONS_FIELD_KEY) ?? null;

  const [values, setValues] = useState<UserFieldsFormValues>(EMPTY_USER_FIELDS_VALUES);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);

  /**
   * セッションごとの参加 QR (`/m/:urlToken/join/:sessionId`) から来た場合、そのセッションを参加セッションの初期選択にする
   * (2026-09-04、旧2 OnboardingPage の pendingSessionId 連携を踏襲)。
   * 地区の一覧に存在するものだけ反映し、利用者が外すこともできる (強制ではない)。
   */
  useEffect(() => {
    if (!urlToken || sessions.length === 0) return;
    const pending = loadPendingSessionId(urlToken);
    if (!pending) return;
    if (!sessions.some((s) => s.id === pending)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedSessions((prev) => (prev.includes(pending) ? prev : [...prev, pending]));
  }, [urlToken, sessions]);

  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitStep, setSubmitStep] = useState<string>('');
  const [showPermissions, setShowPermissions] = useState(false);
  const [requestingPerms, setRequestingPerms] = useState(false);
  const [permResult, setPermResult] = useState<{
    // 'unavailable' = 許可はされているが位置の取得に失敗 (電波不良/タイムアウト等)。許可拒否とは区別する。
    location: 'granted' | 'denied' | 'unavailable' | 'pending';
    orientation: 'granted' | 'denied' | 'unsupported' | 'pending';
  }>({ location: 'pending', orientation: 'pending' });

  function handleStart() {
    // 必須項目チェック（FieldDef.mode === 'required'）
    const missing = findMissingRequired(userFields, values);
    if (missing.length > 0) {
      alert(`未入力の必須項目があります: ${missing.join('、')}`);
      return;
    }
    setShowConsent(true);
  }

  async function handleAgree() {
    if (!consentChecked || !user || !district || districtState?.status !== 'ready') return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitStep('サーバ送信');
    try {
      // Cloud Function 経由でサーバ側書込を行う（atomic、永続キャッシュ依存なし）。
      // 組込項目は dataSettings.userFields に builtIn:true で定義されている前提 (master/dataSettings
      // 駆動)。フォールバックは設けない (degenerate 地区は seed で dataSettings を整備する)。
      const fn = httpsCallable(functions, 'createMembership');
      await fn({
        districtId: district.id,
        nickname: values.builtin.nickname.trim(),
        // 専用欄は項目の型のまま送る (複数選択は配列)。空は null。
        gender: toStoredMembershipProfileValue(values.builtin.gender),
        ageGroup: toStoredMembershipProfileValue(values.builtin.ageGroup),
        relationship: toStoredMembershipProfileValue(values.builtin.relationship),
        attribute: toStoredMembershipProfileValue(values.builtin.attribute),
        profileFieldValues: values.custom,
        joinedSessionIds: selectedSessions,
        consentVersion: CONSENT_VERSION,
      });

      // 参加予定のセッションは使い終わったので削除 (重複反映防止、旧2 踏襲)
      clearPendingSessionId(urlToken);

      // 保存完了 → 同意モーダルを閉じて権限取得画面へ
      setSubmitStep('完了');
      setShowConsent(false);
      setShowPermissions(true);
    } catch (e) {
      const fe = e as { code?: string; message?: string };
      const codeStr = fe.code ?? '';
      const msg = fe.message ?? String(e);
      const stepLabel = submitStep || '不明';
      console.error('failed to save consent', {
        step: stepLabel,
        code: codeStr,
        message: msg,
        error: e,
      });
      setSubmitError(`【${stepLabel}】${codeStr}${codeStr ? ' / ' : ''}${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * 権限を取得：iOS 制約のため方位センサー → 位置情報の順
   * 位置情報は必須、方位はオプション
   */
  async function handleRequestPermissions() {
    setRequestingPerms(true);
    const orientResult = await requestOrientationPermission();
    setPermResult((p) => ({ ...p, orientation: orientResult }));

    try {
      await requestLocation();
      setPermResult((p) => ({ ...p, location: 'granted' }));
      setRequestingPerms(false);
      navigate(`/m/${urlToken}/recovery/issue`);
    } catch (err) {
      // getCurrentPosition は「許可済みでも」TIMEOUT(3)/POSITION_UNAVAILABLE(2) で reject する。
      // それを「許可拒否」と誤判定しないよう、許可そのものの状態を Permissions API で確認する。
      // (Android Chrome で「権限を許可したのに『許可されませんでした』」と出る不具合への対応)
      const code = (err as GeolocationPositionError | undefined)?.code;
      const permState = await getGeolocationPermissionState();
      setRequestingPerms(false);
      if (permState === 'granted') {
        // 許可は下りている。位置の fix は取れなかったが onboarding は通過させる
        // (実際の現在地は投稿時に useGeolocation の継続取得で確定する)。
        setPermResult((p) => ({ ...p, location: 'granted' }));
        navigate(`/m/${urlToken}/recovery/issue`);
      } else if (code === 1 /* PERMISSION_DENIED */ || permState === 'denied') {
        // 本当に拒否されている → ブラウザ設定からの許可を案内
        setPermResult((p) => ({ ...p, location: 'denied' }));
      } else {
        // 拒否ではないが位置を取得できない (電波不良/タイムアウト) → 再試行を促す
        setPermResult((p) => ({ ...p, location: 'unavailable' }));
      }
    }
  }

  function skipPermissionsAndProceed() {
    navigate(`/m/${urlToken}/recovery/issue`);
  }

  if (loading) {
    return (
      <main className="mx-auto max-w-md p-6">
        <p className="text-slate-500 mt-12 text-center">読み込み中…</p>
      </main>
    );
  }

  if (error || !district) {
    return (
      <main className="mx-auto max-w-md p-6">
        <p className="text-danger-700 mt-12">
          地区情報を取得できませんでした。URL をご確認ください。
        </p>
        {error && <p className="text-sm text-slate-500 mt-2">{error}</p>}
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <InstallPrompt />
      <h1 className="text-2xl font-bold mt-6 mb-2">まちあるきへようこそ</h1>
      <p className="text-slate-700 mb-6">📍 {district.name}</p>

      <UserFieldsForm
        fields={userFields}
        values={values}
        onChange={setValues}
        // 2026-09-05: 見出しと入力形式は地区の `sessions` 項目定義 (項目名 / 択一・複数 / 表示タイプ) に従う。
        // 以前はここが「参加セッション」+ チェックボックス固定で、管理画面の設定が反映されなかった。
        renderSessions={() => (
          <SessionPicker
            sessions={sessions}
            field={sessionsField}
            value={selectedSessions}
            onChange={setSelectedSessions}
            showLabel
          />
        )}
      />

      {(() => {
        const missingRequired = findMissingRequired(userFields, values);
        const canStart = missingRequired.length === 0;
        return (
          <>
            {!canStart && (
              <p className="mt-4 text-xs text-warning-700">
                未入力の必須項目: {missingRequired.join('、')}
              </p>
            )}
            <button
              type="button"
              onClick={handleStart}
              disabled={!canStart}
              className="mt-4 w-full bg-primary-600 text-white py-4 rounded-md font-medium disabled:opacity-40"
            >
              はじめる
            </button>
          </>
        );
      })()}

      {showPermissions && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-6 max-h-[90vh] overflow-auto">
            <h2 className="text-xl font-bold mb-4">📍 端末の機能を許可</h2>
            <p className="mb-3 text-slate-700">
              投稿には<strong>位置情報が必須</strong>です。
              <br />
              また、写真の<strong>撮影方向</strong>を記録するため方位センサーも使用します（任意）。
            </p>
            <div className="bg-slate-50 rounded p-3 text-sm mb-4 grid gap-1">
              <PermStatusLine label="位置情報" status={permResult.location} required />
              <PermStatusLine label="方位センサー" status={permResult.orientation} />
            </div>
            <p className="text-xs text-slate-500 mb-4">
              ※ 次のボタンを押すと、ブラウザの許可ダイアログが順番に表示されます。
            </p>
            <button
              type="button"
              onClick={handleRequestPermissions}
              disabled={requestingPerms}
              className="w-full bg-primary-700 text-white py-4 rounded-md font-medium disabled:opacity-40"
            >
              {requestingPerms ? '取得中…' : '権限を許可してはじめる'}
            </button>
            {permResult.location === 'denied' && (
              <>
                <p className="text-danger-700 text-sm mt-3">
                  位置情報が許可されませんでした。ブラウザの設定から許可してください。
                </p>
                <button
                  type="button"
                  onClick={skipPermissionsAndProceed}
                  className="w-full mt-2 text-slate-700 py-3 text-sm underline"
                >
                  あとで設定して進む
                </button>
              </>
            )}
            {permResult.location === 'unavailable' && (
              <>
                <p className="text-warning-700 text-sm mt-3">
                  位置情報の許可はされましたが、現在地を取得できませんでした。電波の良い屋外などで、もう一度上のボタンを押してお試しください。
                </p>
                <button
                  type="button"
                  onClick={skipPermissionsAndProceed}
                  className="w-full mt-2 text-slate-700 py-3 text-sm underline"
                >
                  このまま進む
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showConsent && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-6 max-h-[90vh] overflow-auto">
            <h2 className="text-xl font-bold mb-4">📋 {CONSENT_TEXT.title}</h2>
            <p className="mb-2">{CONSENT_TEXT.purposesIntro}</p>
            <ul className="list-disc list-inside mb-4 space-y-1">
              {CONSENT_TEXT.purposes.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            <p className="text-sm text-slate-700 mb-4">{CONSENT_TEXT.noPersonalInfo}</p>
            {/* docs/28 §5-7: 投稿した写真の取り扱い (自動ぼかし・ぼかし前の写真の保存と削除)。 */}
            <p className="text-sm font-bold text-slate-800 mb-1">
              {CONSENT_TEXT.photoHandlingTitle}
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1 text-sm text-slate-700">
              {CONSENT_TEXT.photoHandling.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            <label className="flex items-start gap-2 mb-6">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 w-5 h-5"
              />
              <span>{CONSENT_TEXT.agreeLabel}</span>
            </label>
            <button
              type="button"
              onClick={handleAgree}
              disabled={!consentChecked || submitting}
              className="w-full bg-primary-700 text-white py-4 rounded-md font-medium disabled:opacity-40"
            >
              {submitting ? `保存中…（${submitStep}）` : CONSENT_TEXT.agreeButton}
            </button>
            {submitError && (
              <div className="mt-3 p-3 bg-danger-50 border border-danger-200 rounded text-danger-700 text-sm">
                <p className="font-medium mb-1">保存に失敗しました</p>
                <p className="text-xs break-all">{submitError}</p>
                <p className="text-xs text-slate-600 mt-2">
                  他のタブですでに別の地区を開いている場合は、それらを閉じてからもう一度お試しください。
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowConsent(false)}
              className="w-full mt-2 text-slate-700 py-3"
            >
              {CONSENT_TEXT.cancelButton}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

function PermStatusLine({
  label,
  status,
  required,
}: {
  label: string;
  status: 'granted' | 'denied' | 'unsupported' | 'unavailable' | 'pending';
  required?: boolean;
}) {
  const icon =
    status === 'granted'
      ? '✅'
      : status === 'denied'
        ? '❌'
        : status === 'unsupported' || status === 'unavailable'
          ? '⚠️'
          : '⏳';
  const text =
    status === 'granted'
      ? '許可済み'
      : status === 'denied'
        ? '拒否'
        : status === 'unsupported'
          ? '非対応'
          : status === 'unavailable'
            ? '取得できず'
            : '未取得';
  return (
    <div className="flex items-center gap-2">
      <span>{icon}</span>
      <span className="font-medium">{label}</span>
      {required && <span className="text-xs text-danger-600">必須</span>}
      <span className="text-slate-500 text-sm ml-auto">{text}</span>
    </div>
  );
}
